/**
 * Role-based utility functions for the frontend
 */

// Admin roles enum
export const ADMIN_ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  VIEWER: "viewer",
};

// Admin permissions enum
export const PERMISSIONS = {
  LEADS_READ: "leads_read",
  LEADS_WRITE: "leads_write",
  LEADS_DELETE: "leads_delete",
  ADMINS_READ: "admins_read",
  ADMINS_WRITE: "admins_write",
  ADMINS_DELETE: "admins_delete",
  SETTINGS_READ: "settings_read",
  SETTINGS_WRITE: "settings_write",
};

/**
 * Check if user has a specific role
 * @param {Object} user - User object from Redux store
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const hasRole = (user, role) => {
  if (!user || !user.role) return false;
  return user.role === role;
};

/**
 * Check if user is a super admin
 * @param {Object} user - User object from Redux store
 * @returns {boolean}
 */
export const isSuperAdmin = (user) => {
  return hasRole(user, ADMIN_ROLES.SUPER_ADMIN);
};

/**
 * Check if user is at least an admin (admin or super admin)
 * @param {Object} user - User object from Redux store
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  if (!user || !user.role) return false;
  return [ADMIN_ROLES.ADMIN, ADMIN_ROLES.SUPER_ADMIN].includes(user.role);
};

/**
 * Check if user can manage other admins
 * @param {Object} user - User object from Redux store
 * @returns {boolean}
 */
export const canManageAdmins = (user) => {
  return isSuperAdmin(user);
};

/**
 * Get permissions based on role
 * @param {string} role - User role
 * @returns {Array} - Array of permissions
 */
export const getPermissionsByRole = (role) => {
  const permissions = {
    [ADMIN_ROLES.SUPER_ADMIN]: [
      PERMISSIONS.LEADS_READ,
      PERMISSIONS.LEADS_WRITE,
      PERMISSIONS.LEADS_DELETE,
      PERMISSIONS.ADMINS_READ,
      PERMISSIONS.ADMINS_WRITE,
      PERMISSIONS.ADMINS_DELETE,
      PERMISSIONS.SETTINGS_READ,
      PERMISSIONS.SETTINGS_WRITE,
    ],
    [ADMIN_ROLES.ADMIN]: [
      PERMISSIONS.LEADS_READ,
      PERMISSIONS.LEADS_WRITE,
      PERMISSIONS.LEADS_DELETE,
      PERMISSIONS.ADMINS_READ,
      PERMISSIONS.SETTINGS_READ,
    ],
    [ADMIN_ROLES.VIEWER]: [PERMISSIONS.LEADS_READ, PERMISSIONS.SETTINGS_READ],
  };

  return permissions[role] || permissions[ADMIN_ROLES.ADMIN];
};

/**
 * Check if user has a specific permission
 * @param {Object} user - User object from Redux store
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  const userPermissions = getPermissionsByRole(user.role);
  return userPermissions.includes(permission);
};

/**
 * Get user's role display name
 * @param {string} role - User role
 * @returns {string}
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    [ADMIN_ROLES.SUPER_ADMIN]: "Super Admin",
    [ADMIN_ROLES.ADMIN]: "Admin",
    [ADMIN_ROLES.VIEWER]: "Viewer",
  };

  return roleNames[role] || "Unknown";
};

/**
 * Get role badge color for UI
 * @param {string} role - User role
 * @returns {string}
 */
export const getRoleBadgeColor = (role) => {
  const colors = {
    [ADMIN_ROLES.SUPER_ADMIN]: "bg-purple-100 text-purple-800",
    [ADMIN_ROLES.ADMIN]: "bg-blue-100 text-blue-800",
    [ADMIN_ROLES.VIEWER]: "bg-gray-100 text-gray-800",
  };

  return colors[role] || "bg-gray-100 text-gray-800";
};
